import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FiDroplet,
  FiCheckCircle,
  FiAlertCircle,
  FiSave,
  FiTrash2,
  FiRefreshCw,
  FiInfo,
  FiEye,
} from 'react-icons/fi';
import { getWhiteLabelConfig, configureWhiteLabel, resetWhiteLabel, deleteWhiteLabelConfig, WhiteLabelConfig } from '../../api/whiteLabel';
import { useWhiteLabel } from '../../context/WhiteLabelContext';
import FeatureGate from '../common/FeatureGate';

const WhiteLabelPage: React.FC = () => {
  const { t } = useTranslation('settings');
  const { reload: reloadWhiteLabel } = useWhiteLabel();
  const [config, setConfig] = useState<WhiteLabelConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    app_name: 'GenIaC',
    company_name: '',
    logo_url: '',
    favicon_url: '',
    primary_color: '#4F46E5',
    secondary_color: '#10B981',
    accent_color: '#F59E0B',
    welcome_message: '',
    footer_text: '',
    support_email: '',
    support_url: '',
    hide_geniac_branding: false,
    custom_domain: '',
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getWhiteLabelConfig();

      if (result.has_config && result.config) {
        setConfig(result.config);
        setFormData({
          app_name: result.config.app_name,
          company_name: result.config.company_name || '',
          logo_url: result.config.logo_url || '',
          favicon_url: result.config.favicon_url || '',
          primary_color: result.config.primary_color,
          secondary_color: result.config.secondary_color,
          accent_color: result.config.accent_color,
          welcome_message: result.config.welcome_message || '',
          footer_text: result.config.footer_text || '',
          support_email: result.config.support_email || '',
          support_url: result.config.support_url || '',
          hide_geniac_branding: result.config.hide_geniac_branding,
          custom_domain: result.config.custom_domain || '',
        });
      }
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError(t('whiteLabel.errors.businessOnly'));
      } else {
        setError(err.response?.data?.error || t('whiteLabel.errors.loadFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Validation des couleurs
      const colorRegex = /^#[0-9A-Fa-f]{6}$/;
      if (!colorRegex.test(formData.primary_color)) {
        setError(t('whiteLabel.errors.invalidPrimaryColor'));
        return;
      }
      if (!colorRegex.test(formData.secondary_color)) {
        setError(t('whiteLabel.errors.invalidSecondaryColor'));
        return;
      }
      if (!colorRegex.test(formData.accent_color)) {
        setError(t('whiteLabel.errors.invalidAccentColor'));
        return;
      }

      const result = await configureWhiteLabel(formData);

      setSuccess(result.message);
      setConfig(result.config);
      reloadWhiteLabel();
    } catch (err: any) {
      const msg = err.response?.data?.message || err.response?.data?.error;
      const status = err.response?.status;
      setError(msg || (status ? `Erreur HTTP ${status}` : `Erreur réseau: ${err.message}`));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(t('whiteLabel.confirmReset'))) {
      return;
    }

    try {
      setResetting(true);
      setError(null);
      setSuccess(null);

      const result = await resetWhiteLabel();
      setSuccess(result.message);
      setConfig(result.config);
      reloadWhiteLabel();
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || t('whiteLabel.errors.resetFailed'));
    } finally {
      setResetting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('whiteLabel.confirmDelete'))) {
      return;
    }

    try {
      setDeleting(true);
      setError(null);
      setSuccess(null);

      const result = await deleteWhiteLabelConfig();
      setSuccess(result.message);
      setConfig(null);

      // Reset form to defaults
      setFormData({
        app_name: 'GenIaC',
        company_name: '',
        logo_url: '',
        favicon_url: '',
        primary_color: '#4F46E5',
        secondary_color: '#10B981',
        accent_color: '#F59E0B',
        welcome_message: '',
        footer_text: '',
        support_email: '',
        support_url: '',
        hide_geniac_branding: false,
        custom_domain: '',
      });
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || t('whiteLabel.errors.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600">{t('whiteLabel.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <FeatureGate feature="white_label" mode="page" lockedMessage="Marque blanche (White Label)">
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-3 bg-purple-100 rounded-xl">
            <FiDroplet className="text-purple-600" size={28} />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('whiteLabel.header.title')}</h1>
            <p className="text-gray-600">{t('whiteLabel.header.subtitle')}</p>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
        <div className="flex items-start gap-3">
          <FiInfo className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
          <div className="text-sm text-blue-800">
            <p className="font-semibold mb-1">{t('whiteLabel.infoBanner.title')}</p>
            <p>{t('whiteLabel.infoBanner.description')}</p>
          </div>
        </div>
      </div>

      {/* Status Card */}
      {config && (
        <div className={`mb-6 rounded-xl border p-4 ${
          config.is_active
            ? 'bg-green-50 border-green-200'
            : 'bg-gray-50 border-gray-200'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {config.is_active ? (
                <FiCheckCircle className="text-green-600" size={24} />
              ) : (
                <FiAlertCircle className="text-gray-600" size={24} />
              )}
              <div>
                <div className="font-semibold text-gray-900">
                  {config.is_active ? t('whiteLabel.status.active') : t('whiteLabel.status.inactive')}
                </div>
                <div className="text-sm text-gray-600">
                  {t('whiteLabel.status.lastModified')}: {new Date(config.updated_at).toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <FiAlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
          <div className="flex-1">
            <h3 className="font-semibold text-red-800">{t('whiteLabel.error.title')}</h3>
            <p className="text-red-600 text-sm mt-1">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">×</button>
        </div>
      )}

      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <FiCheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={20} />
          <div className="flex-1">
            <h3 className="font-semibold text-green-800">{t('whiteLabel.success.title')}</h3>
            <p className="text-green-600 text-sm mt-1">{success}</p>
          </div>
          <button onClick={() => setSuccess(null)} className="text-green-500 hover:text-green-700">×</button>
        </div>
      )}

      {/* Configuration Form */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">{t('whiteLabel.config.title')}</h2>
          <p className="text-sm text-gray-600 mt-1">{t('whiteLabel.config.subtitle')}</p>
        </div>

        <div className="p-6 space-y-8">
          {/* Branding Section */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('whiteLabel.branding.title')}</h3>
            <div className="space-y-4">
              {/* App Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('whiteLabel.branding.appName')}
                </label>
                <input
                  type="text"
                  value={formData.app_name}
                  onChange={(e) => setFormData({ ...formData, app_name: e.target.value })}
                  placeholder="GenIaC"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              {/* Company Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('whiteLabel.branding.companyName')}
                </label>
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  placeholder={t('whiteLabel.branding.companyNamePlaceholder')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              {/* Logo URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('whiteLabel.branding.logoUrl')}
                </label>
                <input
                  type="text"
                  value={formData.logo_url}
                  onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                  placeholder={t('whiteLabel.branding.logoUrlPlaceholder')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('whiteLabel.branding.logoUrlHint')}
                </p>
              </div>

              {/* Favicon URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('whiteLabel.branding.faviconUrl')}
                </label>
                <input
                  type="text"
                  value={formData.favicon_url}
                  onChange={(e) => setFormData({ ...formData, favicon_url: e.target.value })}
                  placeholder={t('whiteLabel.branding.faviconUrlPlaceholder')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Colors Section */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('whiteLabel.colors.title')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Primary Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('whiteLabel.colors.primary')}
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formData.primary_color}
                    onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                    className="h-10 w-20 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.primary_color}
                    onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                    placeholder="#4F46E5"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Secondary Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('whiteLabel.colors.secondary')}
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formData.secondary_color}
                    onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                    className="h-10 w-20 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.secondary_color}
                    onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                    placeholder="#10B981"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Accent Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('whiteLabel.colors.accent')}
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formData.accent_color}
                    onChange={(e) => setFormData({ ...formData, accent_color: e.target.value })}
                    className="h-10 w-20 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.accent_color}
                    onChange={(e) => setFormData({ ...formData, accent_color: e.target.value })}
                    placeholder="#F59E0B"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('whiteLabel.content.title')}</h3>
            <div className="space-y-4">
              {/* Welcome Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('whiteLabel.content.welcomeMessage')}
                </label>
                <textarea
                  value={formData.welcome_message}
                  onChange={(e) => setFormData({ ...formData, welcome_message: e.target.value })}
                  placeholder={t('whiteLabel.content.welcomeMessagePlaceholder')}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              {/* Footer Text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('whiteLabel.content.footerText')}
                </label>
                <input
                  type="text"
                  value={formData.footer_text}
                  onChange={(e) => setFormData({ ...formData, footer_text: e.target.value })}
                  placeholder={t('whiteLabel.content.footerTextPlaceholder')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              {/* Support Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('whiteLabel.content.supportEmail')}
                </label>
                <input
                  type="email"
                  value={formData.support_email}
                  onChange={(e) => setFormData({ ...formData, support_email: e.target.value })}
                  placeholder={t('whiteLabel.content.supportEmailPlaceholder')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              {/* Support URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('whiteLabel.content.supportUrl')}
                </label>
                <input
                  type="text"
                  value={formData.support_url}
                  onChange={(e) => setFormData({ ...formData, support_url: e.target.value })}
                  placeholder={t('whiteLabel.content.supportUrlPlaceholder')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Advanced Section */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('whiteLabel.advanced.title')}</h3>
            <div className="space-y-4">
              {/* Custom Domain */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('whiteLabel.advanced.customDomain')}
                </label>
                <input
                  type="text"
                  value={formData.custom_domain}
                  onChange={(e) => setFormData({ ...formData, custom_domain: e.target.value })}
                  placeholder={t('whiteLabel.advanced.customDomainPlaceholder')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('whiteLabel.advanced.customDomainHint')}
                </p>
              </div>

              {/* Hide GenIaC Branding */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="hide_branding"
                  checked={formData.hide_geniac_branding}
                  onChange={(e) => setFormData({ ...formData, hide_geniac_branding: e.target.checked })}
                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
                <label htmlFor="hide_branding" className="text-sm font-medium text-gray-700">
                  {t('whiteLabel.advanced.hideBranding')}
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="p-6 bg-gray-50 border-t border-gray-200 rounded-b-xl flex justify-between">
          <div className="flex gap-3">
            {config && (
              <>
                <button
                  onClick={handleReset}
                  disabled={resetting}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  <FiRefreshCw className={resetting ? 'animate-spin' : ''} />
                  {resetting ? t('whiteLabel.buttons.resetting') : t('whiteLabel.buttons.reset')}
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  <FiTrash2 />
                  {deleting ? t('whiteLabel.buttons.disabling') : t('whiteLabel.buttons.disable')}
                </button>
              </>
            )}
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            <FiSave />
            {saving ? t('whiteLabel.buttons.saving') : t('whiteLabel.buttons.save')}
          </button>
        </div>
      </div>

      {/* Preview Section */}
      <div className="mt-8 bg-gray-50 rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <FiEye className="text-gray-600" />
          <h3 className="font-semibold text-gray-900">{t('whiteLabel.preview.title')}</h3>
        </div>
        <div className="flex gap-4">
          <div className="flex-1 p-6 rounded-lg" style={{ backgroundColor: formData.primary_color }}>
            <p className="text-white font-semibold">{t('whiteLabel.preview.primary')}</p>
            <p className="text-white text-sm opacity-90">{formData.primary_color}</p>
          </div>
          <div className="flex-1 p-6 rounded-lg" style={{ backgroundColor: formData.secondary_color }}>
            <p className="text-white font-semibold">{t('whiteLabel.preview.secondary')}</p>
            <p className="text-white text-sm opacity-90">{formData.secondary_color}</p>
          </div>
          <div className="flex-1 p-6 rounded-lg" style={{ backgroundColor: formData.accent_color }}>
            <p className="text-white font-semibold">{t('whiteLabel.preview.accent')}</p>
            <p className="text-white text-sm opacity-90">{formData.accent_color}</p>
          </div>
        </div>
      </div>
    </div>
    </FeatureGate>
  );
};

export default WhiteLabelPage;
